import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Paper
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Pets as PetsIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  MedicalServices as MedicalIcon,
  Warning as WarningIcon,
  Description as DocumentIcon,
  Science as ScienceIcon,
  CheckCircle as CheckCircleIcon,
  Link as LinkIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@services/authService';
import { visitsService } from '@services/visitsService';
import { formatDateOnly } from '@utils/dateFormatter';
import { useAuth } from '@contexts/AuthContext';
import * as labResultsService from '@services/labResultsService';
import type { LabOrderWithResults } from '@services/labResultsService';

function parseVitalSigns(vital: {
  temperature: string;
  weight: string;
  heart_rate: string;
  respiratory_rate: string;
}): Record<string, number> | undefined {
  const out: Record<string, number> = {};
  const entries: [keyof typeof vital, string][] = [
    ['temperature', 'temperature'],
    ['weight', 'weight'],
    ['heart_rate', 'heart_rate'],
    ['respiratory_rate', 'respiratory_rate']
  ];
  for (const [key, label] of entries) {
    const raw = vital[key]?.trim();
    if (raw) {
      const n = Number(raw.replace(',', '.'));
      if (!Number.isNaN(n)) {
        out[label] = n;
      }
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

interface Patient {
  id: string;
  name: string;
  species: string;
  breed: string;
  microchipNumber?: string;
  allergies?: string[];
  owner?: any;
  visits?: any[];
  active_problems?: any[];
  medications?: any[];
  consents?: any[];
}

const DoctorPatients: React.FC = () => {
  const navigate = useNavigate();
  const { id: patientId } = useParams();
  const { user } = useAuth();
  const clinicId = (user as { clinicId?: string })?.clinicId;
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [consultationDialogOpen, setConsultationDialogOpen] = useState(false);
  const [patientTab, setPatientTab] = useState(0);
  const [labResults, setLabResults] = useState<LabOrderWithResults[]>([]);
  const [labResultsLoading, setLabResultsLoading] = useState(false);
  const [attachVisitDialogOpen, setAttachVisitDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedVisitId, setSelectedVisitId] = useState('');
  const [consultationData, setConsultationData] = useState({
    chief_complaint: '',
    history: '',
    physical_exam: '',
    diagnosis: '',
    treatment_plan: '',
    recommendations: '',
    vital_signs: {
      temperature: '',
      weight: '',
      heart_rate: '',
      respiratory_rate: ''
    }
  });

  useEffect(() => {
    if (patientId) {
      loadPatientDetails(patientId);
    } else {
      loadPatients();
    }
  }, [patientId]);

  const loadLabResults = async () => {
    if (!patientId || !clinicId) return;
    setLabResultsLoading(true);
    try {
      const data = await labResultsService.getPatientLabResults(patientId, clinicId);
      setLabResults(data);
    } catch (_) {
      setLabResults([]);
    } finally {
      setLabResultsLoading(false);
    }
  };

  useEffect(() => {
    if (patientTab === 1 && patientId && clinicId) loadLabResults();
  }, [patientTab, patientId, clinicId]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/doctor/patients?search=${searchQuery}`);
      setPatients(response.data.data.patients);
    } catch (error) {
      console.error('Eroare la incarcarea pacientilor:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPatientDetails = async (id: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/doctor/patients/${id}`);
      setSelectedPatient(response.data.data);
    } catch (error) {
      console.error('Eroare la incarcarea detaliilor pacientului:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadPatients();
  };

  const handleStartConsultation = () => {
    setConsultationDialogOpen(true);
  };

  const handleSaveConsultation = async () => {
    if (!selectedPatient || !user?.id || !clinicId) {
      alert('Lipsesc datele de clinica sau utilizatorul.');
      return;
    }

    try {
      const vital_signs = parseVitalSigns(consultationData.vital_signs);
      const visit = await visitsService.createVisit({
        clinicId,
        petId: selectedPatient.id,
        ownerId: selectedPatient.owner?.id ?? '',
        doctorId: user.id,
        reason: consultationData.chief_complaint || 'Consultatie',
        notes: consultationData.history || '',
        visitType: 'consultation',
        physical_exam: consultationData.physical_exam || undefined,
        diagnosis: consultationData.diagnosis || undefined,
        treatment_plan: consultationData.treatment_plan || undefined,
        recommendations: consultationData.recommendations || undefined,
        vital_signs
      });

      setConsultationDialogOpen(false);
      loadPatientDetails(selectedPatient.id);
      navigate(`/visits/${visit.id}/encounter`);
    } catch (error) {
      console.error('Eroare la salvarea consultatiei:', error);
      alert('Eroare la salvarea consultatiei');
    }
  };

  // View: Lista pacienti
  if (!patientId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Pacienti (EMR)
        </Typography>

        {/* Search */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <form onSubmit={handleSearch}>
              <Grid container spacing={2} alignItems="center">
                <Grid size="grow">
                  <TextField
                    fullWidth
                    placeholder="Cauta dupa nume, microchip..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid size="grow">
                  <Button variant="contained" type="submit">
                    Cauta
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : patients.length === 0 ? (
          <Alert severity="info">Nu au fost gasiti pacienti</Alert>
        ) : (
          <Grid container spacing={2}>
            {patients.map((patient) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={patient.id}>
                <Card sx={{ cursor: 'pointer' }} onClick={() => navigate(`/doctor/patients/${patient.id}`)}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                        <PetsIcon />
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="h6">{patient.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {patient.species} {patient.breed && `• ${patient.breed}`}
                        </Typography>
                      </Box>
                    </Box>
                    {patient.microchipNumber && (
                      <Typography variant="caption" color="text.secondary">
                        Microchip: {patient.microchipNumber}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  // View: Detalii pacient
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedPatient) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Pacient negasit</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button variant="outlined" onClick={() => navigate('/doctor/patients')}>
            ← Inapoi
          </Button>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <PetsIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">{selectedPatient.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedPatient.species} {selectedPatient.breed && `• ${selectedPatient.breed}`}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleStartConsultation}
        >
          Consultatie Noua
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Info */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            {/* Informatii Generale */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informatii Generale
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Proprietar
                    </Typography>
                    <Typography variant="body1">
                      {selectedPatient.owner?.firstName} {selectedPatient.owner?.lastName}
                    </Typography>
                  </Box>
                  {selectedPatient.microchipNumber && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Microchip
                      </Typography>
                      <Typography variant="body1">
                        {selectedPatient.microchipNumber}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Alergii */}
            {selectedPatient.allergies && selectedPatient.allergies.length > 0 && (
              <Card sx={{ bgcolor: 'error.light' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <WarningIcon color="error" />
                    <Typography variant="h6" color="error">
                      Alergii
                    </Typography>
                  </Box>
                  <Stack spacing={0.5}>
                    {selectedPatient.allergies.map((allergy, index) => (
                      <Chip key={index} label={allergy} color="error" size="small" />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Probleme Active */}
            {selectedPatient.active_problems && selectedPatient.active_problems.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Probleme Active
                  </Typography>
                  <List dense>
                    {selectedPatient.active_problems.map((problem: any, index: number) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={problem.diagnosis}
                          secondary={formatDateOnly(problem.visit_date)}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {/* Medicatie Curenta */}
            {selectedPatient.medications && selectedPatient.medications.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Medicatie Curenta
                  </Typography>
                  <List dense>
                    {selectedPatient.medications.map((med: any) => (
                      <ListItem key={med.id}>
                        <ListItemText
                          primary={med.medication_name}
                          secondary={`${med.dosage} - ${med.frequency}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>

        {/* Right Column - Tabs: Istoric Vizite | Rezultate laborator */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Tabs value={patientTab} onChange={(_, v) => setPatientTab(v)} sx={{ mb: 2 }}>
                <Tab label="Istoric Vizite" />
                <Tab label="Rezultate laborator" icon={<ScienceIcon />} iconPosition="start" />
              </Tabs>

              {patientTab === 0 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Istoric Vizite
                  </Typography>
                  {!selectedPatient.visits || selectedPatient.visits.length === 0 ? (
                    <Alert severity="info">Nu exista vizite in istoric</Alert>
                  ) : (
                    <Stack spacing={2}>
                      {selectedPatient.visits.map((visit: any) => (
                        <Paper key={visit.id} sx={{ p: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {new Date(visit.visit_date).toLocaleDateString('ro-RO', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </Typography>
                            <Chip label={visit.visit_type} size="small" />
                          </Box>

                          {visit.chief_complaint && (
                            <Box mb={1}>
                              <Typography variant="caption" color="text.secondary">
                                Motiv:
                              </Typography>
                              <Typography variant="body2">{visit.chief_complaint}</Typography>
                            </Box>
                          )}

                          {visit.diagnosis && (
                            <Box mb={1}>
                              <Typography variant="caption" color="text.secondary">
                                Diagnostic:
                              </Typography>
                              <Typography variant="body2">{visit.diagnosis}</Typography>
                            </Box>
                          )}

                          {visit.treatment_plan && (
                            <Box mb={1}>
                              <Typography variant="caption" color="text.secondary">
                                Plan Tratament:
                              </Typography>
                              <Typography variant="body2">{visit.treatment_plan}</Typography>
                            </Box>
                          )}

                          {visit.recommendations && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Recomandari:
                              </Typography>
                              <Typography variant="body2">{visit.recommendations}</Typography>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </>
              )}

              {patientTab === 1 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Rezultate laborator
                  </Typography>
                  {labResultsLoading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress />
                    </Box>
                  ) : labResults.length === 0 ? (
                    <Alert severity="info">Nu există rezultate de laborator pentru acest pacient.</Alert>
                  ) : (
                    <Stack spacing={2}>
                      {labResults.map((order) => (
                        <Paper key={order.id} sx={{ p: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                            <Box>
                              <Typography variant="subtitle2">
                                {order.received_at ? formatDateOnly(order.received_at) : formatDateOnly(order.ordered_at)} – {order.lab_provider || 'Laborator'}
                              </Typography>
                              <Chip
                                size="small"
                                label={order.status === 'verified' ? 'Verificat' : order.status === 'received' ? 'Primit' : 'În așteptare'}
                                color={order.status === 'verified' ? 'success' : order.status === 'received' ? 'info' : 'default'}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                            <Stack direction="row" spacing={1}>
                              {order.document_url && (
                                <Button
                                  size="small"
                                  startIcon={<GetAppIcon />}
                                  href={order.document_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  PDF
                                </Button>
                              )}
                              {(user?.role === 'veterinarian' || user?.role === 'clinic_admin') && order.status === 'received' && (
                                <Button
                                  size="small"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={async () => {
                                    try {
                                      await labResultsService.markLabResultReviewed(order.id);
                                      loadLabResults();
                                    } catch (_) {}
                                  }}
                                >
                                  Marchează verificat
                                </Button>
                              )}
                              {!order.visit_id && (selectedPatient?.visits?.length ?? 0) > 0 && (
                                <Button
                                  size="small"
                                  startIcon={<LinkIcon />}
                                  onClick={() => {
                                    setSelectedOrderId(order.id);
                                    setSelectedVisitId('');
                                    setAttachVisitDialogOpen(true);
                                  }}
                                >
                                  Atașează la vizită
                                </Button>
                              )}
                            </Stack>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Attach to visit dialog */}
      <Dialog open={attachVisitDialogOpen} onClose={() => setAttachVisitDialogOpen(false)}>
        <DialogTitle>Atașează rezultat la vizită</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Selectează vizita
            </Typography>
            <Stack spacing={0.5}>
              {selectedPatient?.visits?.map((visit: any) => (
                <Button
                  key={visit.id}
                  variant={selectedVisitId === visit.id ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setSelectedVisitId(visit.id)}
                >
                  {new Date(visit.visit_date).toLocaleDateString('ro-RO')} – {visit.visit_type}
                </Button>
              ))}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachVisitDialogOpen(false)}>Anulare</Button>
          <Button
            variant="contained"
            disabled={!selectedOrderId || !selectedVisitId}
            onClick={async () => {
              if (!selectedOrderId || !selectedVisitId) return;
              try {
                await labResultsService.attachLabResultToVisit(selectedOrderId, selectedVisitId);
                setAttachVisitDialogOpen(false);
                loadLabResults();
              } catch (_) {}
            }}
          >
            Atașează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Consultation Dialog */}
      <Dialog open={consultationDialogOpen} onClose={() => setConsultationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Consultatie Noua - {selectedPatient.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Motiv Consultatie (Chief Complaint)"
              value={consultationData.chief_complaint}
              onChange={(e) => setConsultationData({ ...consultationData, chief_complaint: e.target.value })}
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="Anamneza / Istoric"
              value={consultationData.history}
              onChange={(e) => setConsultationData({ ...consultationData, history: e.target.value })}
              multiline
              rows={3}
            />

            {/* Semne Vitale */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Semne Vitale
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Temperatura (°C)"
                    value={consultationData.vital_signs.temperature}
                    onChange={(e) =>
                      setConsultationData({
                        ...consultationData,
                        vital_signs: { ...consultationData.vital_signs, temperature: e.target.value }
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Greutate (kg)"
                    value={consultationData.vital_signs.weight}
                    onChange={(e) =>
                      setConsultationData({
                        ...consultationData,
                        vital_signs: { ...consultationData.vital_signs, weight: e.target.value }
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Puls (bpm)"
                    value={consultationData.vital_signs.heart_rate}
                    onChange={(e) =>
                      setConsultationData({
                        ...consultationData,
                        vital_signs: { ...consultationData.vital_signs, heart_rate: e.target.value }
                      })
                    }
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Respiratie (/min)"
                    value={consultationData.vital_signs.respiratory_rate}
                    onChange={(e) =>
                      setConsultationData({
                        ...consultationData,
                        vital_signs: { ...consultationData.vital_signs, respiratory_rate: e.target.value }
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <TextField
              fullWidth
              label="Examen Clinic"
              value={consultationData.physical_exam}
              onChange={(e) => setConsultationData({ ...consultationData, physical_exam: e.target.value })}
              multiline
              rows={4}
            />

            <TextField
              fullWidth
              label="Diagnostic"
              value={consultationData.diagnosis}
              onChange={(e) => setConsultationData({ ...consultationData, diagnosis: e.target.value })}
              multiline
              rows={2}
            />

            <TextField
              fullWidth
              label="Plan Tratament"
              value={consultationData.treatment_plan}
              onChange={(e) => setConsultationData({ ...consultationData, treatment_plan: e.target.value })}
              multiline
              rows={3}
            />

            <TextField
              fullWidth
              label="Recomandari"
              value={consultationData.recommendations}
              onChange={(e) => setConsultationData({ ...consultationData, recommendations: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsultationDialogOpen(false)}>Anuleaza</Button>
          <Button variant="outlined">Salveaza Draft</Button>
          <Button variant="contained" onClick={handleSaveConsultation}>
            Salveaza & Semneaza
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorPatients;

